
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, ArrowRight, Clock } from 'lucide-react';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from '@/components/ui/sonner';
import { isValidEmail } from '@/contexts/auth/utils/validationUtils';

// Form schema for password reset request
const resetRequestSchema = z.object({
  email: z.string().email("Please enter a valid email address").min(1, "Email is required"),
});

type ResetRequestValues = z.infer<typeof resetRequestSchema>;

interface RequestResetFormProps {
  onReset: (email: string) => Promise<void>;
  isLoading: boolean;
}

export const RequestResetForm: React.FC<RequestResetFormProps> = ({ onReset, isLoading }) => {
  // Form for requesting password reset
  const requestForm = useForm<ResetRequestValues>({
    resolver: zodResolver(resetRequestSchema),
    defaultValues: {
      email: '',
    },
  });

  const handleRequestReset = async (values: ResetRequestValues) => {
    // Check if user exists with this email
    if (!isValidEmail(values.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    await onReset(values.email);
  };

  return (
    <Form {...requestForm}>
      <form onSubmit={requestForm.handleSubmit(handleRequestReset)} className="space-y-4">
        <FormField
          control={requestForm.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    className="pl-10"
                    disabled={isLoading}
                    {...field}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <Button 
          type="submit" 
          className="w-full"
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              {isLoading && !requestForm.formState.isSubmitting ? (
                <>
                  <Clock className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Please wait...
                </>
              ) : (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              )}
            </span>
          ) : (
            <span className="flex items-center justify-center">
              Send Reset Instructions
              <ArrowRight className="ml-2 h-4 w-4" />
            </span>
          )}
        </Button>
      </form>
    </Form>
  );
};
